/**
 * TASK-cc4f60b3: minimal path routing for the routerless SPA.
 *
 * Only the three email-linked surfaces (TASK-0ff4036d deep links) get real
 * URLs — everything else keeps the historic single-URL behavior and stays on
 * '/' so the default first paint is unchanged:
 *
 *   /                -> quote (default, same as before routing)
 *   /quote           -> quote (explicit alias)
 *   /orders          -> orders   (order-card surface from order emails)
 *   /orders/:id      -> orders + orderId (s7 per-order Guid deep link —
 *                       the email CTA opens OrdersPage focused on that order)
 *   /provider        -> provider (provider portal from provider emails)
 *   /provider/orders -> provider + orders tab (F3: actionable provider
 *                       notification pushes land on the queue, not Профиль)
 *   /provider/products/new  -> provider + products tab + create page
 *   /provider/products/<id> -> provider + products tab + edit page
 *                              (0e895f9a: product editing is a separate
 *                              route, not the inline panel form)
 *   /admin           -> admin    (users tab)
 *   /admin/disputes  -> admin + disputes tab (admin dispute fan-out emails)
 *   anything else    -> quote, unmatched (deep-link refresh on an unknown
 *                       path renders the default page exactly like the
 *                       routerless app did)
 *
 * The s7 per-order link carries the full order Guid (email templates show
 * it next to the 8-char display number); OrdersPage resolves the focus —
 * an unknown or foreign Guid silently falls back to the plain list there.
 */

export type Page =
  | 'quote'
  | 'shop'
  | 'providers'
  | 'product'
  | 'cart'
  | 'orders'
  | 'favorites'
  | 'provider'
  | 'auth'
  | 'admin'
  // 2f9c70ba: referral landing — /r/<code|slug> resolves to the referring
  // performer's shop (subdomain visits land here through the proxy).
  | 'referral';

export type AdminTab = 'users' | 'categories' | 'orders' | 'disputes' | 'settings';

// F3: provider portal tabs (canonical union lives next to the provider
// types; re-exported here so Route stays self-describing like AdminTab).
export type { ProviderTab } from './types/provider';
import type { ProviderTab } from './types/provider';

export type Route = {
  page: Page;
  /** Initial admin tab when page === 'admin', null otherwise. */
  adminTab: AdminTab | null;
  /** F3: initial provider tab when page === 'provider', null otherwise
   *  (same null-rules as adminTab — only /provider/orders carries one). */
  providerTab: ProviderTab | null;
  /** 0e895f9a: product edit target when page === 'provider' — 'new' or a
   *  listing Guid from /provider/products/<id>; null otherwise. */
  providerProductId: string | null;
  /** Per-order Guid from an /orders/:id deep link (s7); null otherwise. */
  orderId: string | null;
  /** 2f9c70ba: referral code or slug from /r/:code; null otherwise. */
  referralCode: string | null;
  /** True when the path is one of the explicit routes above. */
  matched: boolean;
};

/** Normalize a raw href: drop query/hash and any trailing slash. */
function normalizePath(href: string): string {
  const noQuery = href.split(/[?#]/, 1)[0] ?? href;
  const path = noQuery.endsWith('/') && noQuery !== '/' ? noQuery.slice(0, -1) : noQuery;
  return path === '' ? '/' : path;
}

/** Base route literal with null extras (spread overrides below). */
const baseRoute = (): Route => ({
  page: 'quote',
  adminTab: null,
  providerTab: null,
  providerProductId: null,
  orderId: null,
  referralCode: null,
  matched: true,
});

/** Resolve a pathname (e.g. window.location.pathname) to a page route. */
export function pathToPage(href: string): Route {
  const path = normalizePath(href);
  switch (path) {
    case '/':
    case '/quote':
      return baseRoute();
    case '/orders':
      return { ...baseRoute(), page: 'orders' };
    case '/provider':
      return { ...baseRoute(), page: 'provider' };
    // F3: exact-path entry like /admin/disputes — actionable provider
    // notifications (new order, result confirmed, dispute legs) open the
    // queue tab directly. No other tabs get URLs for now; deeper unknown
    // /provider/* paths keep the fallback below.
    case '/provider/orders':
      return { ...baseRoute(), page: 'provider', providerTab: 'orders' };
    case '/admin':
      return { ...baseRoute(), page: 'admin' };
    case '/admin/disputes':
      return { ...baseRoute(), page: 'admin', adminTab: 'disputes' };
  }
  const segments = path.split('/').filter(Boolean);
  // 0e895f9a: provider product edit page — /provider/products/new (create)
  // and /provider/products/<guid> (edit). Exactly three segments; deeper or
  // shorter paths stay unknown (same fallback as any unrecognized URL).
  if (segments.length === 3 && segments[0] === 'provider' && segments[1] === 'products') {
    return { ...baseRoute(), page: 'provider', providerProductId: safeDecode(segments[2]) };
  }
  // s7: per-order deep link — /orders/<guid> opens the orders page focused
  // on that order. Exactly one segment after /orders; deeper paths stay
  // unknown (same fallback as any unrecognized URL).
  if (segments.length === 2 && segments[0] === 'orders') {
    return { ...baseRoute(), page: 'orders', orderId: safeDecode(segments[1]) };
  }
  // 2f9c70ba: referral landing — /r/<code|slug> opens the referring
  // performer's shop (vanity subdomains land here through the reverse
  // proxy rewrite). Exactly one segment after /r.
  if (segments.length === 2 && segments[0] === 'r') {
    return { ...baseRoute(), page: 'referral', referralCode: safeDecode(segments[1]) };
  }
  // Unknown path: keep the default first paint; the URL is left as-is
  // (no rewrite), matching the pre-routing behavior for index.html fallback.
  return { ...baseRoute(), matched: false };
}

/** Decode a path segment; malformed escapes keep the raw text. */
function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** URL for a page. Pages without a route stay on '/' (client-only nav). */
export function pageToPath(page: Page): string {
  switch (page) {
    case 'orders':
      return '/orders';
    case 'provider':
      return '/provider';
    case 'admin':
      return '/admin';
    default:
      return '/';
  }
}
